import * as THREE from 'three';
import type { ProjectileState, SimEvent, Team } from '@moba/shared';
import { makeGlowTexture } from './assets.ts';
import { ISO_PITCH, ISO_YAW } from './CameraRig.ts';

/** giro que pone de cara a la cámara isométrica un objeto modelado mirando a +Z */
const FACE_CAMERA_YAW = ISO_YAW - Math.PI;
import { disposeGroup, makeGiantSword, makeTriforce, setGroupOpacity } from './TriforceFx.ts';

const TEAM_HEX: Record<Team, number> = {
  blue: 0x4da3ff,
  red: 0xff5a5a,
};

/** distancias (unidades) para atenuar el temblor de pantalla */
const SHAKE_NEAR = 6;
const SHAKE_FAR = 18;

const HEAL_COLOR = 0x6dff9a;
/** segundos entre cruces mientras el héroe se cura */
const HEAL_INTERVAL = 0.12;
/** cuánto sigue emitiendo tras el último tick de curación (evita parpadeo) */
const HEAL_LINGER = 0.22;

const PROJECTILE_COLOR: Record<ProjectileState['kind'], number> = {
  orb: 0xff9a9a,
  arrow: 0xd9ffb0,
  missile: 0xffb86b,
  needle: 0xc9f2ff,
  fire: 0xff7a3c,
  bomb: 0x9a9aa6,
  blackbomb: 0x1a1a1f,
  blaster: 0xffb86b,
  tower: 0xfff3a6,
  test: 0xffffff,
};

interface Effect {
  object: THREE.Object3D;
  life: number;
  maxLife: number;
  update: (progress: number, dt: number) => void;
  dispose: () => void;
}

interface DamageNumber {
  el: HTMLDivElement;
  x: number;
  y: number;
  z: number;
  life: number;
  maxLife: number;
}

interface ProjectileVisual {
  group: THREE.Group;
  core: THREE.Mesh;
  glow: THREE.Sprite;
  lastTrail: number;
  startX: number;
  startZ: number;
  /** bomba de Link: bola que rueda, punta de la mecha, chispa y botes dados */
  ball?: THREE.Object3D;
  fuseTip?: THREE.Object3D;
  spark?: THREE.Sprite;
  bounce?: number;
}

/** radio visual de la bomba de Link y altura desde la que sale de la mano */
const BOMB_RADIUS = 0.34;
const BOMB_THROW_HEIGHT = 0.9;
/** botes de la bomba: [largo del tramo, altura del arco]; luego rueda */
const BOMB_BOUNCES: Array<[number, number]> = [[4.2, 1.2], [2.7, 0.5], [1.7, 0.2], [1.1, 0.07]];

/** Altura (sobre su radio) de la bomba tras recorrer `d` y número de bote actual. */
function bombHeight(d: number): { height: number; bounce: number } {
  let start = 0;
  for (let i = 0; i < BOMB_BOUNCES.length; i++) {
    const [length, peak] = BOMB_BOUNCES[i];
    if (d < start + length) {
      const u = (d - start) / length;
      const from = i === 0 ? BOMB_THROW_HEIGHT : 0;
      return { height: from * (1 - u) + 4 * peak * u * (1 - u), bounce: i };
    }
    start += length;
  }
  return { height: 0, bounce: BOMB_BOUNCES.length };
}

/** Segundos que dura un emoji de la rueda sobre el héroe. */
const EMOTE_LIFE = 2.6;

/** Eventos de efectos cortos que no se crean si ocurren fuera de cámara. */
const OFFSCREEN_SKIPPABLE = new Set<SimEvent['type']>([
  'attack',
  'hit',
  'projectileSpawn',
  'projectileHit',
  'projectileExplode',
  'reflect',
  'levelUp',
  'minionDeath',
  'respawn',
]);

/**
 * Geometrías compartidas de los efectos más frecuentes (anillos de impacto,
 * marcador de movimiento): antes cada golpe creaba y subía a la GPU su propio
 * anillo y lo borraba medio segundo después. Las compartidas ignoran `dispose`.
 */
const sharedGeometries = new Map<string, THREE.BufferGeometry>();
function flatGeometry(key: string, make: () => THREE.BufferGeometry): THREE.BufferGeometry {
  let geometry = sharedGeometries.get(key);
  if (!geometry) {
    geometry = make();
    geometry.rotateX(-Math.PI / 2);
    geometry.dispose = () => {};
    sharedGeometries.set(key, geometry);
  }
  return geometry;
}

export class Vfx {
  shake = 0;

  private readonly effects: Effect[] = [];
  private readonly numbers: DamageNumber[] = [];
  private readonly projectiles = new Map<number, ProjectileVisual>();
  private readonly glowTexture = makeGlowTexture();
  private readonly crossTexture = makeCrossTexture();
  private readonly toastEl: HTMLElement | null;
  private listenerX = 0;
  private listenerZ = 0;
  private listenerId = -1;
  private toastTimer = 0;
  private time = 0;
  private healX = 0;
  private healZ = 0;
  private healUntil = 0;
  private healTimer = 0;
  private readonly tmpVec = new THREE.Vector3();
  /** Trifuerzas de la R de Link en curso, por héroe que la lanzó */
  private readonly triforces = new Map<number, { burst: () => void }>();
  /** posición interpolada de una unidad (la Trifuerza sigue a su objetivo) */
  unitPosition?: (id: number) => { x: number; z: number } | undefined;
  /** si la unidad se ve (niebla de guerra); sin callback = siempre */
  unitVisible?: (id: number) => boolean;
  /** si el punto del suelo cae en pantalla (con margen); sin callback = siempre */
  onScreen?: (x: number, z: number) => boolean;
  /** emojis de la rueda (T) encima de cada héroe: uno por héroe, el nuevo reemplaza al anterior */
  private readonly emotes = new Map<number, { el: HTMLElement; life: number }>();

  constructor(
    private readonly scene: THREE.Scene,
    private readonly layer: HTMLElement,
  ) {
    this.toastEl = document.getElementById('toast');
  }

  play(event: SimEvent): void {
    // efectos cortos fuera de cámara: no se crean (nadie los vería); el temblor sí cuenta
    const at = event as { x?: number; z?: number };
    if (OFFSCREEN_SKIPPABLE.has(event.type) && at.x !== undefined && at.z !== undefined && this.onScreen && !this.onScreen(at.x, at.z)) {
      if (event.type === 'hit') {
        this.addShake(event.heavy ? 0.5 : 0.16, 1.2, event.x, event.z, event.sourceId === this.listenerId || event.targetId === this.listenerId);
      } else if (event.type === 'projectileExplode' && event.kind !== 'blackbomb') {
        this.addShake(0.25, 1.2, event.x, event.z, false);
      }
      if (event.type !== 'projectileExplode' || event.kind !== 'blackbomb') return;
    }
    switch (event.type) {
      case 'attack':
        this.slash(event);
        break;
      case 'hit':
        this.impact(event.x, event.z, event.heavy ? 0xffb86b : 0xffd166, event.heavy);
        this.number(
          event.x,
          event.z,
          event.amount,
          event.heavy ? 'heavy' : 'normal',
        );
        this.addShake(event.heavy ? 0.5 : 0.16, 1.2, event.x, event.z,
          event.sourceId === this.listenerId || event.targetId === this.listenerId);
        break;
      case 'death':
        this.death(event.x, event.z);
        this.addShake(0.55, 1.4, event.x, event.z, event.heroId === this.listenerId);
        break;
      case 'respawn':
        this.pulse(event.x, event.z, 0x6fb6ff, 2.4);
        break;
      case 'projectileSpawn':
        this.muzzle(event.x, event.z, event.kind);
        break;
      case 'projectileHit':
        this.impact(event.x, event.z, 0xff9d6b, false);
        break;
      case 'projectileExplode':
        if (event.kind === 'blackbomb') {
          this.bombExplosion(event.x, event.z, event.radius ?? 3);
          break;
        }
        this.impact(event.x, event.z, 0xffb86b, true);
        this.addShake(0.25, 1.2, event.x, event.z, false);
        break;
      case 'reflect':
        this.impact(event.x, event.z, 0x6fe3ff, true);
        this.pulse(event.x, event.z, 0x4fd8ff, 1.6);
        break;
      case 'levelUp':
        this.pulse(event.x, event.z, 0xc9a2ff, 2.2);
        break;
      case 'recall':
        if (event.phase === 'done') {
          // llegada a la base (el evento ya trae la posición de destino)
          this.pulse(event.x, event.z, 0x9fe0ff, 3.2);
          this.recallArrive(event.x, event.z);
        }
        if (event.phase === 'cancel') this.pulse(event.x, event.z, 0x6a7a8a, 1.2);
        break;
      case 'execute':
        if (event.phase === 'mark') this.triforceMark(event.heroId, event.targetId, event.x, event.z, event.delay / 30);
        else this.swordStrike(event.heroId, event.x, event.z, event.heroId === this.listenerId || event.targetId === this.listenerId);
        break;
      case 'minionDeath':
        this.pulse(event.x, event.z, event.team === 'blue' ? 0x4da3ff : 0xff5a5a, 1.4);
        break;
      case 'structureDestroyed':
        this.death(event.x, event.z);
        this.pulse(event.x, event.z, 0xffd166, event.kind === 'nexus' ? 9 : 6);
        this.addShake(1.0, 1.6, event.x, event.z, false);
        break;
    }
  }

  /** Posición y héroe del jugador local: el temblor de pantalla depende de ellos. */
  setListener(x: number, z: number, heroId: number): void {
    this.listenerX = x;
    this.listenerZ = z;
    this.listenerId = heroId;
  }

  /**
   * El héroe se está curando: emite cruces verdes que suben y palpitan.
   * Llamar cada frame mientras la vida suba; el emisor se apaga solo tras
   * HEAL_LINGER sin nuevas llamadas.
   */
  showHealing(x: number, z: number): void {
    this.healX = x;
    this.healZ = z;
    this.healUntil = this.time + HEAL_LINGER;
  }

  private updateHealing(dt: number): void {
    this.healTimer -= dt;
    if (this.time >= this.healUntil) {
      this.healTimer = Math.max(0, this.healTimer);
      return;
    }
    if (this.healTimer > 0) return;
    this.healTimer = HEAL_INTERVAL;
    this.spawnHealCross();
  }

  private spawnHealCross(): void {
    const angle = Math.random() * Math.PI * 2;
    const radius = 0.15 + Math.random() * 0.45;
    const baseX = this.healX + Math.cos(angle) * radius;
    const baseZ = this.healZ + Math.sin(angle) * radius;
    const baseY = 0.55 + Math.random() * 0.5;
    const driftX = (Math.random() - 0.5) * 0.22;
    const driftZ = (Math.random() - 0.5) * 0.22;
    const phase = Math.random() * Math.PI * 2;
    const scale = 0.34 + Math.random() * 0.16;

    const material = new THREE.SpriteMaterial({
      map: this.crossTexture,
      color: HEAL_COLOR,
      transparent: true,
      opacity: 0,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      depthTest: false,
    });
    const sprite = new THREE.Sprite(material);
    sprite.position.set(baseX, baseY, baseZ);
    sprite.scale.setScalar(scale);
    this.scene.add(sprite);

    const life = 0.8 + Math.random() * 0.35;
    this.effects.push({
      object: sprite,
      life,
      maxLife: life,
      update: (progress) => {
        sprite.position.set(baseX + driftX * progress, baseY + progress * 1.5, baseZ + driftZ * progress);
        const pulse = 1 + Math.sin(progress * Math.PI * 4 + phase) * 0.22;
        sprite.scale.setScalar(scale * pulse * (1 + progress * 0.3));
        material.opacity = Math.sin(progress * Math.PI) * 0.95;
      },
      dispose: () => material.dispose(),
    });
  }

  /**
   * Temblor atenuado por distancia al jugador: completo a menos de
   * SHAKE_NEAR, nulo más allá de SHAKE_FAR. Si el jugador participa, completo.
   */
  addShake(amount: number, cap: number, x: number, z: number, involvesPlayer: boolean): void {
    let factor = 1;
    if (!involvesPlayer) {
      const dist = Math.hypot(x - this.listenerX, z - this.listenerZ);
      factor = 1 - THREE.MathUtils.clamp((dist - SHAKE_NEAR) / (SHAKE_FAR - SHAKE_NEAR), 0, 1);
    }
    if (factor <= 0) return;
    this.shake = Math.min(cap, this.shake + amount * factor);
  }

  playMoveMarker(x: number, z: number): void {
    const ringGeometry = flatGeometry('move-ring', () => new THREE.RingGeometry(0.26, 0.38, 32));
    const ringMaterial = new THREE.MeshBasicMaterial({
      color: 0x7ce6c0,
      transparent: true,
      opacity: 0.9,
      side: THREE.DoubleSide, forceSinglePass: true,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    });
    const ring = new THREE.Mesh(ringGeometry, ringMaterial);
    ring.position.set(x, 0.07, z);
    this.scene.add(ring);

    this.effects.push({
      object: ring,
      life: 0.55,
      maxLife: 0.55,
      update: (progress) => {
        ring.scale.setScalar(1 + progress * 1.5);
        ringMaterial.opacity = 0.9 * (1 - progress);
      },
      dispose: () => {
        ringGeometry.dispose();
        ringMaterial.dispose();
      },
    });

    const dotGeometry = flatGeometry('move-dot', () => new THREE.CircleGeometry(0.11, 16));
    const dotMaterial = new THREE.MeshBasicMaterial({
      color: 0x7ce6c0,
      transparent: true,
      opacity: 0.85,
      side: THREE.DoubleSide, forceSinglePass: true,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    });
    const dot = new THREE.Mesh(dotGeometry, dotMaterial);
    dot.position.set(x, 0.08, z);
    this.scene.add(dot);

    this.effects.push({
      object: dot,
      life: 0.55,
      maxLife: 0.55,
      update: (progress) => {
        dotMaterial.opacity = 0.85 * (1 - progress);
      },
      dispose: () => {
        dotGeometry.dispose();
        dotMaterial.dispose();
      },
    });
  }

  syncProjectiles(projectiles: ProjectileState[], alpha: number): void {
    const seen = new Set<number>();

    for (const projectile of projectiles) {
      seen.add(projectile.id);
      let visual = this.projectiles.get(projectile.id);
      if (!visual) {
        visual = this.createProjectileVisual(projectile);
        this.projectiles.set(projectile.id, visual);
      }

      const x = projectile.prevPos.x + (projectile.pos.x - projectile.prevPos.x) * alpha;
      const z = projectile.prevPos.z + (projectile.pos.z - projectile.prevPos.z) * alpha;
      if (projectile.kind === 'fire') {
        // la bola de fuego de Mario va botando por el suelo
        const travelled = Math.hypot(x - visual.startX, z - visual.startZ);
        visual.group.position.set(x, 0.3 + Math.abs(Math.sin((travelled / 3.2) * Math.PI)) * 1.1, z);
      } else {
        visual.group.position.set(x, 0.55, z);
      }
      if (projectile.kind === 'blackbomb') {
        this.updateBomb(visual, projectile, x, z);
        continue;
      }
      if (projectile.kind === 'arrow') {
        // la flecha apunta hacia donde vuela (también al rebotar en un Reflector)
        const vx = projectile.vel.x;
        const vz = projectile.vel.z;
        if (vx * vx + vz * vz > 1e-6) visual.group.rotation.y = Math.atan2(vx, vz);
        visual.glow.scale.setScalar(0.6);
        continue;
      }
      const pulse = 1 + Math.sin(this.time * 18 + projectile.id) * 0.08;
      visual.glow.scale.setScalar(1.9 * pulse);

      if (this.time - visual.lastTrail > 0.045) {
        visual.lastTrail = this.time;
        if (!this.onScreen || this.onScreen(x, z)) this.trail(x, z, projectile.team);
      }
    }

    for (const [id, visual] of this.projectiles) {
      if (seen.has(id)) continue;
      this.scene.remove(visual.group);
      visual.group.traverse((object) => {
        const mesh = object as THREE.Mesh | THREE.Sprite;
        if (!(mesh as THREE.Mesh).isMesh && !(mesh as THREE.Sprite).isSprite) return;
        if ((mesh as THREE.Mesh).isMesh) mesh.geometry.dispose();
        (mesh.material as THREE.Material).dispose();
      });
      this.projectiles.delete(id);
    }
  }

  update(dt: number, camera: THREE.Camera, width: number, height: number): void {
    this.time += dt;
    this.updateHealing(dt);

    for (const effect of this.effects) {
      effect.life -= dt;
      const progress = 1 - Math.max(0, effect.life) / effect.maxLife;
      effect.update(progress, dt);
    }
    for (let i = this.effects.length - 1; i >= 0; i--) {
      const effect = this.effects[i];
      if (effect.life > 0) continue;
      this.scene.remove(effect.object);
      effect.dispose();
      this.effects.splice(i, 1);
    }

    this.updateNumbers(dt, camera, width, height);
    this.updateEmotes(dt, camera, width, height);

    if (this.toastTimer > 0) {
      this.toastTimer -= dt;
      if (this.toastTimer <= 0) this.toastEl?.classList.remove('show');
    }

    this.shake = Math.max(0, this.shake - dt * 2.6);
  }

  private slash(event: Extract<SimEvent, { type: 'attack' }>): void {
    const color =
      event.slot === 'attack' ? 0x9fe3ff : event.heavy ? 0xffd08a : 0x7fb8ff;
    const segments = Math.max(14, Math.round(event.arc / (Math.PI / 36)));
    const inner = Math.max(0.28, event.range * 0.34);
    const outer = event.range;

    const group = new THREE.Group();
    group.position.set(event.x, 0.07, event.z);
    group.rotation.y = event.facing;

    const vertices: number[] = [];
    const indices: number[] = [];
    for (let i = 0; i <= segments; i++) {
      const angle = -event.arc / 2 + (event.arc * i) / segments;
      const sin = Math.sin(angle);
      const cos = Math.cos(angle);
      vertices.push(sin * inner, 0, cos * inner, sin * outer, 0, cos * outer);
      if (i < segments) {
        const base = i * 2;
        indices.push(base, base + 1, base + 2, base + 1, base + 3, base + 2);
      }
    }

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.Float32BufferAttribute(vertices, 3));
    geometry.setIndex(indices);

    const fillMaterial = new THREE.MeshBasicMaterial({
      color,
      transparent: true,
      opacity: 0.5,
      side: THREE.DoubleSide, forceSinglePass: true,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    });
    group.add(new THREE.Mesh(geometry, fillMaterial));

    const edgeVertices: number[] = [];
    for (let i = 0; i <= segments; i++) {
      const angle = -event.arc / 2 + (event.arc * i) / segments;
      edgeVertices.push(Math.sin(angle) * outer, 0.02, Math.cos(angle) * outer);
    }
    const edgeGeometry = new THREE.BufferGeometry();
    edgeGeometry.setAttribute('position', new THREE.Float32BufferAttribute(edgeVertices, 3));
    const edgeMaterial = new THREE.LineBasicMaterial({
      color: 0xffffff,
      transparent: true,
      opacity: 0.85,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    });
    group.add(new THREE.Line(edgeGeometry, edgeMaterial));

    this.scene.add(group);
    this.effects.push({
      object: group,
      life: 0.26,
      maxLife: 0.26,
      update: (progress) => {
        group.scale.setScalar(0.94 + progress * 0.12);
        fillMaterial.opacity = 0.5 * (1 - progress);
        edgeMaterial.opacity = 0.85 * (1 - progress);
      },
      dispose: () => {
        geometry.dispose();
        fillMaterial.dispose();
        edgeGeometry.dispose();
        edgeMaterial.dispose();
      },
    });
  }

  private impact(x: number, z: number, color: number, heavy: boolean): void {
    const geometry = flatGeometry('impact-ring', () => new THREE.RingGeometry(0.18, 0.3, 28));
    const material = new THREE.MeshBasicMaterial({
      color,
      transparent: true,
      opacity: 0.9,
      side: THREE.DoubleSide, forceSinglePass: true,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    });
    const ring = new THREE.Mesh(geometry, material);
    ring.position.set(x, 0.09, z);
    this.scene.add(ring);

    this.effects.push({
      object: ring,
      life: heavy ? 0.36 : 0.26,
      maxLife: heavy ? 0.36 : 0.26,
      update: (progress) => {
        ring.scale.setScalar(1 + progress * (heavy ? 3.6 : 2.2));
        material.opacity = 0.9 * (1 - progress);
      },
      dispose: () => {
        geometry.dispose();
        material.dispose();
      },
    });

    this.sparks(x, z, color, heavy ? 18 : 8, heavy ? 5.2 : 3.2, heavy ? 0.5 : 0.32);
  }

  private sparks(
    x: number,
    z: number,
    color: number,
    count: number,
    speed: number,
    life: number,
  ): void {
    const positions = new Float32Array(count * 3);
    const velocities = new Float32Array(count * 3);

    for (let i = 0; i < count; i++) {
      positions[i * 3] = x;
      positions[i * 3 + 1] = 0.85 + Math.random() * 0.6;
      positions[i * 3 + 2] = z;
      const angle = Math.random() * Math.PI * 2;
      const horizontal = Math.random() * speed;
      velocities[i * 3] = Math.sin(angle) * horizontal;
      velocities[i * 3 + 1] = 2 + Math.random() * speed;
      velocities[i * 3 + 2] = Math.cos(angle) * horizontal;
    }

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    const material = new THREE.PointsMaterial({
      color,
      size: 0.11,
      transparent: true,
      opacity: 0.95,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
      sizeAttenuation: true,
    });
    const points = new THREE.Points(geometry, material);
    this.scene.add(points);

    this.effects.push({
      object: points,
      life,
      maxLife: life,
      update: (progress, dt) => {
        for (let i = 0; i < count; i++) {
          velocities[i * 3 + 1] -= 13 * dt;
          positions[i * 3] += velocities[i * 3] * dt;
          positions[i * 3 + 1] += velocities[i * 3 + 1] * dt;
          positions[i * 3 + 2] += velocities[i * 3 + 2] * dt;
        }
        geometry.attributes.position.needsUpdate = true;
        material.opacity = 0.95 * (1 - progress);
      },
      dispose: () => {
        geometry.dispose();
        material.dispose();
      },
    });
  }

  private pulse(x: number, z: number, color: number, maxScale: number): void {
    const geometry = flatGeometry('pulse-ring', () => new THREE.RingGeometry(0.25, 0.38, 40));
    const material = new THREE.MeshBasicMaterial({
      color,
      transparent: true,
      opacity: 0.85,
      side: THREE.DoubleSide, forceSinglePass: true,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    });
    const ring = new THREE.Mesh(geometry, material);
    ring.position.set(x, 0.1, z);
    this.scene.add(ring);

    this.effects.push({
      object: ring,
      life: 0.42,
      maxLife: 0.42,
      update: (progress) => {
        ring.scale.setScalar(1 + progress * maxScale);
        material.opacity = 0.85 * (1 - progress);
      },
      dispose: () => {
        geometry.dispose();
        material.dispose();
      },
    });
  }

  private death(x: number, z: number): void {
    this.pulse(x, z, 0xff7a5a, 3.6);
    this.sparks(x, z, 0xff9a6b, 30, 6.4, 0.7);
  }

  private muzzle(x: number, z: number, kind: ProjectileState['kind']): void {
    const color = PROJECTILE_COLOR[kind] ?? 0xffffff;
    this.pulse(x, z, color, 1.6);
  }

  /**
   * R de Link, marca: la Trifuerza aparece en lo alto sobre el objetivo, gira y
   * baja hasta quedar sobre su cabeza (de cara a la cámara) mientras un anillo
   * dorado se cierra en el suelo. La sigue durante toda la espera.
   */
  private triforceMark(casterId: number, targetId: number, x: number, z: number, delay: number): void {
    this.triforces.get(casterId)?.burst();
    const group = new THREE.Group();
    const triforce = makeTriforce(2.2);
    // de cara a la cámara e inclinada hacia ella, como un emblema en pantalla
    triforce.rotation.order = 'YXZ';
    triforce.rotation.x = -ISO_PITCH;
    group.add(triforce);
    const halo = new THREE.Sprite(
      new THREE.SpriteMaterial({
        map: this.glowTexture,
        color: 0xffc94a,
        transparent: true,
        opacity: 0,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
      }),
    );
    halo.scale.setScalar(4.2);
    group.add(halo);
    group.position.set(x, 7, z);
    this.scene.add(group);

    const ringGeometry = new THREE.RingGeometry(0.85, 1, 48);
    ringGeometry.rotateX(-Math.PI / 2);
    const ringMaterial = new THREE.MeshBasicMaterial({
      color: 0xffd25a,
      transparent: true,
      opacity: 0,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      side: THREE.DoubleSide,
    });
    const ring = new THREE.Mesh(ringGeometry, ringMaterial);
    ring.position.set(x, 0.08, z);
    this.scene.add(ring);

    const hold = 0.35;
    let bursting = false;
    let burstAt = 0;
    let age = 0;
    const effect: Effect = {
      object: group,
      life: delay + hold,
      maxLife: delay + hold,
      update: (_progress, dt) => {
        age += dt;
        const p = Math.min(1, age / Math.max(0.01, delay));
        const at = this.unitPosition?.(targetId);
        const tx = at?.x ?? group.position.x;
        const tz = at?.z ?? group.position.z;
        if (bursting) {
          // la espada la atraviesa: destello y se deshace hacia fuera
          const b = Math.min(1, (age - burstAt) / 0.18);
          triforce.scale.setScalar(1.15 + b * 0.9);
          setGroupOpacity(triforce, 1 - b);
          (halo.material as THREE.SpriteMaterial).opacity = 0.9 * (1 - b);
          halo.scale.setScalar(4.2 + b * 4);
          ringMaterial.opacity = 0.8 * (1 - b);
          return;
        }
        const ease = 1 - (1 - p) ** 3;
        group.position.set(tx, 7 - ease * 3.3, tz);
        triforce.scale.setScalar(Math.min(1.15, easeOutBack(Math.min(1, p * 2.4)) * 1.15));
        // gira rápido al aparecer y se frena de cara a la cámara isométrica
        triforce.rotation.y = FACE_CAMERA_YAW + Math.max(0, 1 - p / 0.75) ** 2 * Math.PI * 4;
        const shimmer = 0.5 + 0.5 * Math.sin(age * 22);
        (halo.material as THREE.SpriteMaterial).opacity = (0.35 + 0.25 * p + 0.1 * shimmer) * Math.min(1, p * 4);
        ring.position.set(tx, 0.08, tz);
        ring.scale.setScalar(2.6 - 1.6 * ease);
        ringMaterial.opacity = Math.min(1, p * 3) * 0.85;
      },
      dispose: () => {
        disposeGroup(group);
        this.scene.remove(ring);
        ringGeometry.dispose();
        ringMaterial.dispose();
        if (this.triforces.get(casterId)?.burst === burst) this.triforces.delete(casterId);
      },
    };
    const burst = (): void => {
      if (bursting) return;
      bursting = true;
      burstAt = age;
      effect.life = Math.min(effect.life, 0.2);
    };
    this.triforces.set(casterId, { burst });
    this.effects.push(effect);
  }

  /**
   * R de Link, impacto: una espada gigante cae en picado desde el cielo y se
   * clava sobre el objetivo; destello dorado, onda en el suelo, astillas de luz
   * y temblor. La espada queda clavada un momento y se desvanece.
   */
  private swordStrike(casterId: number, x: number, z: number, involvesPlayer: boolean): void {
    this.triforces.get(casterId)?.burst();
    const sword = makeGiantSword(5.6);
    sword.rotation.y = FACE_CAMERA_YAW;
    sword.position.set(x, 10, z);
    this.scene.add(sword);
    const fall = 0.11;
    const stay = 0.6;
    const fade = 0.35;
    let landed = false;
    this.effects.push({
      object: sword,
      life: fall + stay + fade,
      maxLife: fall + stay + fade,
      update: (progress) => {
        const t = progress * (fall + stay + fade);
        if (t < fall) {
          const f = t / fall;
          sword.position.y = 10 - f * f * 10.5;
          return;
        }
        if (!landed) {
          landed = true;
          this.swordImpact(x, z, involvesPlayer);
        }
        const out = Math.max(0, (t - fall - stay) / fade);
        sword.position.y = -0.5 - out * 0.6;
        setGroupOpacity(sword, 1 - out);
      },
      dispose: () => disposeGroup(sword),
    });
  }

  private swordImpact(x: number, z: number, involvesPlayer: boolean): void {
    this.pulse(x, z, 0xffd25a, 6);
    this.pulse(x, z, 0xffffff, 3);
    this.sparks(x, z, 0xffd86b, 26, 7, 0.6);
    this.addShake(0.9, 1.4, x, z, involvesPlayer);

    const flash = new THREE.Sprite(
      new THREE.SpriteMaterial({
        map: this.glowTexture,
        color: 0xfff1c0,
        transparent: true,
        opacity: 1,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
      }),
    );
    flash.position.set(x, 1.2, z);
    this.scene.add(flash);
    this.effects.push({
      object: flash,
      life: 0.3,
      maxLife: 0.3,
      update: (progress) => {
        flash.scale.setScalar(2 + progress * 6);
        flash.material.opacity = 1 - progress;
      },
      dispose: () => flash.material.dispose(),
    });

    // astillas doradas de la Trifuerza que saltan y caen
    for (let i = 0; i < 10; i++) {
      const shard = new THREE.Mesh(
        new THREE.CircleGeometry(0.22, 3),
        new THREE.MeshBasicMaterial({ color: 0xffd04a, transparent: true, side: THREE.DoubleSide, depthWrite: false }),
      );
      const angle = (i / 10) * Math.PI * 2 + Math.random() * 0.4;
      const speed = 3 + Math.random() * 3;
      const up = 4 + Math.random() * 3;
      const spin = (Math.random() - 0.5) * 18;
      shard.position.set(x, 3.4, z);
      this.scene.add(shard);
      this.effects.push({
        object: shard,
        life: 0.7,
        maxLife: 0.7,
        update: (progress) => {
          const t = progress * 0.7;
          shard.position.set(x + Math.cos(angle) * speed * t, Math.max(0.1, 3.4 + up * t - 9 * t * t), z + Math.sin(angle) * speed * t);
          shard.rotation.set(spin * t, spin * 0.7 * t, 0);
          (shard.material as THREE.MeshBasicMaterial).opacity = 1 - progress;
        },
        dispose: () => {
          shard.geometry.dispose();
          (shard.material as THREE.Material).dispose();
        },
      });
    }
  }

  /** Llegada del regreso a base: destello y chispas de luz que suben y se apagan. */
  private recallArrive(x: number, z: number): void {
    const flash = new THREE.Sprite(
      new THREE.SpriteMaterial({
        map: this.glowTexture,
        color: 0xdff4ff,
        transparent: true,
        opacity: 0.9,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
      }),
    );
    flash.position.set(x, 1, z);
    this.scene.add(flash);
    this.effects.push({
      object: flash,
      life: 0.35,
      maxLife: 0.35,
      update: (progress) => {
        flash.scale.setScalar(1.5 + progress * 3.5);
        flash.material.opacity = 0.9 * (1 - progress);
      },
      dispose: () => flash.material.dispose(),
    });

    for (let i = 0; i < 16; i++) {
      const spark = new THREE.Sprite(
        new THREE.SpriteMaterial({
          map: this.glowTexture,
          color: i % 3 === 0 ? 0xffffff : 0x9fe0ff,
          transparent: true,
          opacity: 0,
          blending: THREE.AdditiveBlending,
          depthWrite: false,
        }),
      );
      const angle = (i / 16) * Math.PI * 2;
      const radius = 0.5 + (i % 4) * 0.25;
      const rise = 1.6 + (i % 5) * 0.35;
      const size = 0.22 + (i % 3) * 0.08;
      this.scene.add(spark);
      this.effects.push({
        object: spark,
        life: 0.8,
        maxLife: 0.8,
        update: (progress) => {
          const out = 1 - (1 - progress) ** 2;
          const a = angle + progress * 1.2;
          spark.position.set(x + Math.cos(a) * radius * (0.6 + out * 0.6), 0.2 + out * rise, z + Math.sin(a) * radius * (0.6 + out * 0.6));
          spark.scale.setScalar(size * (1 - progress * 0.6));
          spark.material.opacity = Math.min(1, progress * 8) * (1 - progress);
        },
        dispose: () => spark.material.dispose(),
      });
    }
  }

  private trail(x: number, z: number, team: Team): void {
    const sprite = new THREE.Sprite(
      new THREE.SpriteMaterial({
        map: this.glowTexture,
        color: TEAM_HEX[team],
        transparent: true,
        opacity: 0.5,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
      }),
    );
    sprite.position.set(x, 0.55, z);
    sprite.scale.setScalar(1.1);
    this.scene.add(sprite);

    this.effects.push({
      object: sprite,
      life: 0.22,
      maxLife: 0.22,
      update: (progress) => {
        sprite.scale.setScalar(1.1 * (1 - progress * 0.7));
        (sprite.material as THREE.SpriteMaterial).opacity = 0.5 * (1 - progress);
      },
      dispose: () => {
        (sprite.material as THREE.SpriteMaterial).dispose();
      },
    });
  }

  private number(
    x: number,
    z: number,
    amount: number,
    kind: 'normal' | 'heavy' | 'counter' | 'gold',
  ): void {
    const el = document.createElement('div');
    el.className = kind === 'normal' ? 'dmg' : `dmg ${kind}`;
    el.textContent = kind === 'gold' ? `+${Math.round(amount)}` : String(Math.round(amount));
    this.layer.appendChild(el);

    this.numbers.push({
      el,
      x: x + (Math.random() - 0.5) * 0.4,
      y: 1.75 + Math.random() * 0.35,
      z: z + (Math.random() - 0.5) * 0.4,
      life: 0.95,
      maxLife: 0.95,
    });
  }

  /** Muestra un emoji sobre la cabeza de un héroe durante unos segundos. */
  emote(heroId: number, emoji: string): void {
    this.emotes.get(heroId)?.el.remove();
    const el = document.createElement('div');
    el.className = 'emote-pop';
    el.textContent = emoji;
    this.layer.appendChild(el);
    this.emotes.set(heroId, { el, life: EMOTE_LIFE });
  }

  private updateEmotes(dt: number, camera: THREE.Camera, width: number, height: number): void {
    const projected = new THREE.Vector3();
    for (const [heroId, emote] of this.emotes) {
      emote.life -= dt;
      const at = this.unitPosition?.(heroId);
      if (emote.life <= 0 || !at) {
        emote.el.remove();
        this.emotes.delete(heroId);
        continue;
      }
      const age = EMOTE_LIFE - emote.life;
      // sale con un pequeño rebote, flota y se desvanece al final
      const pop = age < 0.18 ? 0.4 + (age / 0.18) * 0.8 : age < 0.3 ? 1.2 - ((age - 0.18) / 0.12) * 0.2 : 1;
      projected.set(at.x, 3.7 + age * 0.12, at.z).project(camera);
      const screenX = (projected.x * 0.5 + 0.5) * width;
      const screenY = (-projected.y * 0.5 + 0.5) * height;
      const visible = projected.z < 1 && (this.unitVisible?.(heroId) ?? true);
      emote.el.style.transform = `translate3d(${screenX}px, ${screenY}px, 0) translate(-50%, -100%) scale(${pop})`;
      emote.el.style.opacity = visible ? String(Math.min(1, emote.life / 0.4)) : '0';
    }
  }

  private updateNumbers(dt: number, camera: THREE.Camera, width: number, height: number): void {
    const projected = new THREE.Vector3();

    for (let i = this.numbers.length - 1; i >= 0; i--) {
      const number = this.numbers[i];
      number.life -= dt;
      number.y += dt * 1.35;

      if (number.life <= 0) {
        number.el.remove();
        this.numbers.splice(i, 1);
        continue;
      }

      projected.set(number.x, number.y, number.z).project(camera);
      const screenX = (projected.x * 0.5 + 0.5) * width;
      const screenY = (-projected.y * 0.5 + 0.5) * height;
      const progress = 1 - number.life / number.maxLife;
      const visible = projected.z < 1;

      number.el.style.transform = `translate3d(${screenX}px, ${screenY}px, 0) translate(-50%, -50%) scale(${1 + progress * 0.22})`;
      number.el.style.opacity = visible ? String(Math.min(1, (1 - progress) * 1.7)) : '0';
    }
  }

  private createProjectileVisual(projectile: ProjectileState): ProjectileVisual {
    const color = PROJECTILE_COLOR[projectile.kind] ?? TEAM_HEX[projectile.team];
    const group = new THREE.Group();
    if (projectile.kind === 'arrow') return this.createArrowVisual(projectile, group);
    if (projectile.kind === 'blackbomb') return this.createBombVisual(projectile, group);

    const core = new THREE.Mesh(
      new THREE.SphereGeometry(projectile.radius * 0.62, 12, 12),
      new THREE.MeshBasicMaterial({ color }),
    );
    group.add(core);

    const glow = new THREE.Sprite(
      new THREE.SpriteMaterial({
        map: this.glowTexture,
        color,
        transparent: true,
        opacity: 0.9,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
      }),
    );
    glow.scale.setScalar(1.9);
    group.add(glow);

    group.position.set(projectile.pos.x, 0.55, projectile.pos.z);
    this.scene.add(group);

    return { group, core, glow, lastTrail: this.time, startX: projectile.pos.x, startZ: projectile.pos.z };
  }

  /**
   * Hacia dónde mira un proyectil recién creado: su velocidad o, si aún no se
   * movió (online el primer snapshot puede llegar quieto), del tirador hacia él.
   */
  private flightYaw(projectile: ProjectileState): number {
    const vx = projectile.vel.x;
    const vz = projectile.vel.z;
    if (vx * vx + vz * vz > 1e-6) return Math.atan2(vx, vz);
    const owner = this.unitPosition?.(projectile.ownerId);
    if (owner) {
      const dx = projectile.pos.x - owner.x;
      const dz = projectile.pos.z - owner.z;
      if (dx * dx + dz * dz > 1e-4) return Math.atan2(dx, dz);
    }
    return 0;
  }

  /**
   * Flecha de verdad (arco de Link): astil de madera, punta de acero y tres
   * plumas, modelada a lo largo de +Z; `syncProjectiles` la orienta según su
   * velocidad. Sólo un brillo tenue del equipo en la punta, sin estela de bolas.
   */
  private createArrowVisual(projectile: ProjectileState, group: THREE.Group): ProjectileVisual {
    const length = 1.7;
    const shaft = new THREE.Mesh(
      new THREE.CylinderGeometry(0.055, 0.055, length, 6),
      new THREE.MeshStandardMaterial({ color: 0xa0703e, roughness: 0.75 }),
    );
    shaft.rotation.x = Math.PI / 2;
    group.add(shaft);

    const head = new THREE.Mesh(
      new THREE.ConeGeometry(0.15, 0.38, 4),
      new THREE.MeshStandardMaterial({ color: 0xd8dde4, metalness: 0.85, roughness: 0.3 }),
    );
    head.rotation.x = Math.PI / 2;
    head.position.z = length / 2 + 0.18;
    group.add(head);

    // plumas: tres aletas triangulares repartidas a 120° alrededor del astil
    const fletchShape = new THREE.Shape();
    fletchShape.moveTo(0, 0);
    fletchShape.lineTo(0, 0.45);
    fletchShape.lineTo(0.2, 0.06);
    fletchShape.lineTo(0.2, -0.03);
    fletchShape.closePath();
    const fletchGeometry = new THREE.ShapeGeometry(fletchShape);
    for (let i = 0; i < 3; i++) {
      const fletch = new THREE.Mesh(
        i === 0 ? fletchGeometry : fletchGeometry.clone(),
        new THREE.MeshStandardMaterial({ color: i === 0 ? 0xc8352b : 0xf2efe6, side: THREE.DoubleSide, roughness: 0.9 }),
      );
      const pivot = new THREE.Group();
      pivot.rotation.z = (i * Math.PI * 2) / 3;
      fletch.rotation.x = Math.PI / 2;
      fletch.position.set(0.02, 0, -length / 2 + 0.02);
      pivot.add(fletch);
      group.add(pivot);
    }

    const glow = new THREE.Sprite(
      new THREE.SpriteMaterial({
        map: this.glowTexture,
        color: TEAM_HEX[projectile.team],
        transparent: true,
        opacity: 0.22,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
      }),
    );
    glow.position.z = length / 2 + 0.15;
    glow.scale.setScalar(0.6);
    group.add(glow);

    group.rotation.y = this.flightYaw(projectile);
    group.position.set(projectile.pos.x, 0.55, projectile.pos.z);
    this.scene.add(group);
    return { group, core: shaft, glow, lastTrail: this.time, startX: projectile.pos.x, startZ: projectile.pos.z };
  }

  /**
   * Bomba de Link: bola negra con brillo, boquilla metálica, mecha curvada y
   * chispa en la punta. `syncProjectiles` la hace botar (cada bote más bajo) y
   * rodar hacia donde se lanzó, soltando chispas de la mecha.
   */
  private createBombVisual(projectile: ProjectileState, group: THREE.Group): ProjectileVisual {
    const radius = BOMB_RADIUS;
    const ball = new THREE.Group();
    group.add(ball);
    const body = new THREE.Mesh(
      new THREE.SphereGeometry(radius, 20, 16),
      new THREE.MeshStandardMaterial({ color: 0x14161c, roughness: 0.32, metalness: 0.35 }),
    );
    ball.add(body);
    const shine = new THREE.Mesh(
      new THREE.SphereGeometry(radius * 0.22, 10, 8),
      new THREE.MeshBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0.35 }),
    );
    shine.position.set(-radius * 0.45, radius * 0.5, radius * 0.55);
    shine.scale.set(1, 0.6, 0.4);
    ball.add(shine);
    const cap = new THREE.Mesh(
      new THREE.CylinderGeometry(radius * 0.32, radius * 0.38, radius * 0.3, 12),
      new THREE.MeshStandardMaterial({ color: 0x6d6f78, roughness: 0.4, metalness: 0.7 }),
    );
    cap.position.y = radius * 0.98;
    ball.add(cap);
    const fuseCurve = new THREE.CatmullRomCurve3([
      new THREE.Vector3(0, radius * 1.1, 0),
      new THREE.Vector3(radius * 0.08, radius * 1.45, 0.02),
      new THREE.Vector3(radius * 0.32, radius * 1.7, -radius * 0.05),
      new THREE.Vector3(radius * 0.55, radius * 1.78, -radius * 0.1),
    ]);
    const fuse = new THREE.Mesh(
      new THREE.TubeGeometry(fuseCurve, 12, radius * 0.06, 6, false),
      new THREE.MeshStandardMaterial({ color: 0xc8a46a, roughness: 0.9 }),
    );
    ball.add(fuse);
    const fuseTip = new THREE.Object3D();
    fuseTip.position.copy(fuseCurve.getPoint(1));
    ball.add(fuseTip);
    const spark = new THREE.Sprite(
      new THREE.SpriteMaterial({
        map: this.glowTexture,
        color: 0xffb03a,
        transparent: true,
        opacity: 1,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
      }),
    );
    spark.scale.setScalar(0.45);
    fuseTip.add(spark);

    const glow = new THREE.Sprite(
      new THREE.SpriteMaterial({ map: this.glowTexture, color: 0xffb03a, transparent: true, opacity: 0, depthWrite: false }),
    );
    glow.visible = false;
    group.add(glow);

    group.position.set(projectile.pos.x, BOMB_THROW_HEIGHT + radius, projectile.pos.z);
    const vx = projectile.vel.x;
    const vz = projectile.vel.z;
    group.rotation.y = vx * vx + vz * vz > 1e-6 ? Math.atan2(vx, vz) : 0;
    this.scene.add(group);
    return {
      group,
      core: body,
      glow,
      lastTrail: this.time,
      startX: projectile.pos.x,
      startZ: projectile.pos.z,
      ball,
      fuseTip,
      spark,
      bounce: 0,
    };
  }

  /** Bote y rodar de la bomba según la distancia recorrida; chispas de la mecha. */
  private updateBomb(visual: ProjectileVisual, projectile: ProjectileState, x: number, z: number): void {
    const travelled = Math.hypot(x - visual.startX, z - visual.startZ);
    const { height, bounce } = bombHeight(travelled);
    visual.group.position.set(x, BOMB_RADIUS + height, z);
    const vx = projectile.vel.x;
    const vz = projectile.vel.z;
    if (vx * vx + vz * vz > 1e-6) visual.group.rotation.y = Math.atan2(vx, vz);
    // rueda hacia delante: gira sobre su eje lateral lo que avanza / radio
    if (visual.ball) visual.ball.rotation.x = travelled / BOMB_RADIUS;
    if (bounce !== visual.bounce) {
      // cada vez que toca el suelo: polvo
      visual.bounce = bounce;
      this.bombDust(x, z, bounce);
    }
    if (visual.spark) {
      const flicker = 0.35 + Math.random() * 0.25;
      visual.spark.scale.setScalar(flicker);
      visual.spark.material.color.setHex(Math.random() < 0.5 ? 0xffd25a : 0xff8a2a);
    }
    if (visual.fuseTip && this.time - visual.lastTrail > 0.035) {
      visual.lastTrail = this.time;
      visual.fuseTip.getWorldPosition(this.tmpVec);
      this.fuseSpark(this.tmpVec.x, this.tmpVec.y, this.tmpVec.z);
    }
  }

  private fuseSpark(x: number, y: number, z: number): void {
    const sprite = new THREE.Sprite(
      new THREE.SpriteMaterial({
        map: this.glowTexture,
        color: Math.random() < 0.5 ? 0xffe08a : 0xff9a3a,
        transparent: true,
        opacity: 1,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
      }),
    );
    const vx = (Math.random() - 0.5) * 1.6;
    const vy = 0.8 + Math.random() * 1.4;
    const vz = (Math.random() - 0.5) * 1.6;
    const size = 0.08 + Math.random() * 0.08;
    sprite.position.set(x, y, z);
    sprite.scale.setScalar(size);
    this.scene.add(sprite);
    this.effects.push({
      object: sprite,
      life: 0.3,
      maxLife: 0.3,
      update: (progress) => {
        const t = progress * 0.3;
        sprite.position.set(x + vx * t, y + vy * t - 4 * t * t, z + vz * t);
        sprite.material.opacity = 1 - progress;
      },
      dispose: () => sprite.material.dispose(),
    });
  }

  private bombDust(x: number, z: number, bounce: number): void {
    const strength = Math.max(0.3, 1 - bounce * 0.25);
    for (let i = 0; i < 6; i++) {
      const sprite = new THREE.Sprite(
        new THREE.SpriteMaterial({ map: this.glowTexture, color: 0xb9ab8f, transparent: true, opacity: 0.45 * strength, depthWrite: false }),
      );
      const angle = (i / 6) * Math.PI * 2 + Math.random();
      sprite.position.set(x, 0.15, z);
      this.scene.add(sprite);
      this.effects.push({
        object: sprite,
        life: 0.45,
        maxLife: 0.45,
        update: (progress) => {
          const r = 0.2 + progress * 0.9 * strength;
          sprite.position.set(x + Math.cos(angle) * r, 0.15 + progress * 0.3, z + Math.sin(angle) * r);
          sprite.scale.setScalar((0.35 + progress * 0.5) * strength);
          sprite.material.opacity = 0.45 * strength * (1 - progress);
        },
        dispose: () => sprite.material.dispose(),
      });
    }
  }

  /**
   * Explosión de la bomba de Link: destello, bola de fuego que se expande,
   * humo oscuro que sube, chispas y cascotes, onda en el suelo y quemadura.
   */
  private bombExplosion(x: number, z: number, radius: number): void {
    this.addShake(0.6, 1.4, x, z, false);
    this.pulse(x, z, 0xffc86b, radius * 2.2);
    this.sparks(x, z, 0xffb347, 34, 9, 0.75);

    const flash = new THREE.Sprite(
      new THREE.SpriteMaterial({ map: this.glowTexture, color: 0xfff3c4, transparent: true, opacity: 1, blending: THREE.AdditiveBlending, depthWrite: false }),
    );
    flash.position.set(x, 0.9, z);
    this.scene.add(flash);
    this.effects.push({
      object: flash,
      life: 0.22,
      maxLife: 0.22,
      update: (progress) => {
        flash.scale.setScalar(2 + progress * radius * 3);
        flash.material.opacity = 1 - progress;
      },
      dispose: () => flash.material.dispose(),
    });

    // bola de fuego: llamaradas que salen hacia fuera y arriba
    for (let i = 0; i < 14; i++) {
      const angle = Math.random() * Math.PI * 2;
      const out = radius * (0.35 + Math.random() * 0.55);
      const up = 0.6 + Math.random() * 1.6;
      const size = 0.9 + Math.random() * 0.9;
      const life = 0.4 + Math.random() * 0.25;
      const sprite = new THREE.Sprite(
        new THREE.SpriteMaterial({
          map: this.glowTexture,
          color: [0xffe27a, 0xffa23a, 0xff6a1f][i % 3],
          transparent: true,
          opacity: 1,
          blending: THREE.AdditiveBlending,
          depthWrite: false,
        }),
      );
      sprite.position.set(x, 0.5, z);
      this.scene.add(sprite);
      this.effects.push({
        object: sprite,
        life,
        maxLife: life,
        update: (progress) => {
          const e = 1 - (1 - progress) ** 2;
          sprite.position.set(x + Math.cos(angle) * out * e, 0.5 + up * e, z + Math.sin(angle) * out * e);
          sprite.scale.setScalar(size * (0.6 + e * 0.9));
          sprite.material.opacity = 1 - progress;
        },
        dispose: () => sprite.material.dispose(),
      });
    }

    // humo: bocanadas oscuras que suben despacio y se abren
    for (let i = 0; i < 12; i++) {
      const angle = Math.random() * Math.PI * 2;
      const out = radius * (0.2 + Math.random() * 0.6);
      const up = 1.4 + Math.random() * 1.8;
      const size = 1 + Math.random() * 0.9;
      const life = 1.1 + Math.random() * 0.6;
      const delay = 0.08 + Math.random() * 0.12;
      const gray = Math.random() < 0.5 ? 0x2a2a2e : 0x45444a;
      const sprite = new THREE.Sprite(
        new THREE.SpriteMaterial({ map: this.glowTexture, color: gray, transparent: true, opacity: 0, depthWrite: false }),
      );
      sprite.position.set(x, 0.6, z);
      this.scene.add(sprite);
      this.effects.push({
        object: sprite,
        life,
        maxLife: life,
        update: (progress) => {
          const t = progress * life;
          if (t < delay) return;
          const p = (t - delay) / (life - delay);
          const e = 1 - (1 - p) ** 2;
          sprite.position.set(x + Math.cos(angle) * out * e, 0.6 + up * e, z + Math.sin(angle) * out * e);
          sprite.scale.setScalar(size * (0.7 + e * 1.1));
          sprite.material.opacity = 0.7 * Math.min(1, p * 5) * (1 - p);
        },
        dispose: () => sprite.material.dispose(),
      });
    }

    // cascotes: trocitos oscuros que saltan y caen
    for (let i = 0; i < 10; i++) {
      const rock = new THREE.Mesh(
        new THREE.BoxGeometry(0.12, 0.1, 0.12),
        new THREE.MeshBasicMaterial({ color: i % 2 ? 0x3a3530 : 0x1d1b1a, transparent: true }),
      );
      const angle = Math.random() * Math.PI * 2;
      const speed = 3 + Math.random() * 4;
      const up = 4 + Math.random() * 4;
      rock.position.set(x, 0.4, z);
      this.scene.add(rock);
      this.effects.push({
        object: rock,
        life: 0.75,
        maxLife: 0.75,
        update: (progress) => {
          const t = progress * 0.75;
          rock.position.set(x + Math.cos(angle) * speed * t, Math.max(0.05, 0.4 + up * t - 12 * t * t), z + Math.sin(angle) * speed * t);
          rock.rotation.set(t * 14, t * 9, 0);
          (rock.material as THREE.MeshBasicMaterial).opacity = progress > 0.7 ? (1 - progress) / 0.3 : 1;
        },
        dispose: () => {
          rock.geometry.dispose();
          (rock.material as THREE.Material).dispose();
        },
      });
    }

    // quemadura en el suelo que se desvanece
    const scorchGeometry = new THREE.CircleGeometry(radius * 0.75, 32);
    scorchGeometry.rotateX(-Math.PI / 2);
    const scorchMaterial = new THREE.MeshBasicMaterial({ map: this.glowTexture, color: 0x0b0806, transparent: true, opacity: 0.6, depthWrite: false });
    const scorch = new THREE.Mesh(scorchGeometry, scorchMaterial);
    scorch.position.set(x, 0.04, z);
    this.scene.add(scorch);
    this.effects.push({
      object: scorch,
      life: 2.6,
      maxLife: 2.6,
      update: (progress) => {
        scorchMaterial.opacity = 0.6 * (1 - progress);
      },
      dispose: () => {
        scorchGeometry.dispose();
        scorchMaterial.dispose();
      },
    });
  }

  /** "+N" dorado sobre el punto donde se ganó oro. */
  goldGain(x: number, z: number, amount: number): void {
    this.number(x, z, amount, 'gold');
  }

  /** Mensaje grande en pantalla (torre/nexo destruido, etc.). */
  announce(text: string, color: string): void {
    this.toast(text, color);
    this.toastTimer = 2.2;
  }

  private toast(text: string, color: string): void {
    if (!this.toastEl) return;
    this.toastEl.textContent = text;
    this.toastEl.style.color = color;
    this.toastEl.classList.remove('show');
    void this.toastEl.offsetWidth;
    this.toastEl.classList.add('show');
    this.toastTimer = 0.95;
  }
}

/** Textura blanca con una cruz (se tiñe de verde en el material del sprite). */
function makeCrossTexture(size = 64): THREE.CanvasTexture {
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas 2D no disponible');

  const center = size / 2;
  const bar = size * 0.26;
  const length = size * 0.66;
  ctx.clearRect(0, 0, size, size);
  ctx.fillStyle = '#ffffff';
  ctx.shadowColor = 'rgba(160, 255, 190, 0.9)';
  ctx.shadowBlur = size * 0.14;
  ctx.fillRect(center - bar / 2, center - length / 2, bar, length);
  ctx.fillRect(center - length / 2, center - bar / 2, length, bar);

  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  return texture;
}

function easeOutBack(t: number): number {
  const c1 = 1.70158;
  const c3 = c1 + 1;
  return 1 + c3 * (t - 1) ** 3 + c1 * (t - 1) ** 2;
}
